import React, { Suspense, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { sideBarMenu } from '../../Assets/Constants/json_constants.js';
import { fetchMenuDetail } from '../../Services/api-config.js';
import { routerData } from '../../utils/util.js';
import permissions from '../../utils/permissions.js';
import { AUTHKEY } from '../../utils/keys.js';
import { updateRedirectRoute, updateUserInfo } from '../../Store/AuthSlice/auth-slice.js';
import { routeConstants } from '../../utils/path-constants.js';

const allowedPages = new Set(['uam', 'dashboard', 'wallet']);

const UnProtectedLayout = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const userInfo = useSelector((state) => state.auth.userInfo);
	const menu = userInfo?.menu;

	const { viewEsignStats, viewDigitalStampStats } = permissions();

	const getPermissions = (menu, category, subcategory, permissionName) => {
		const subcategoryPermissions = menu?.[category]?.[subcategory] || [];
		if (permissionName) {
			return subcategoryPermissions.some(
				(item) => item.permissionName === permissionName
			);
		}

		return subcategoryPermissions.length > 0;
	};

	const getRedirectPermission = (redirectPage, menu) => {
		switch (redirectPage) {
		case 'dashboard': {
			return menu?.Approval?.dashboard_approval_request;
		}

		case 'uam': {
			return menu?.Approval?.uam_approval_request;
		}

		case 'wallet': {
			return menu?.Approval?.wallet_approval_request;
		}

		default: {
			return false;
		}
		}
	};

	useEffect(() => {
		const isResetPasswordPage = location.pathname === routeConstants.RESET_PASSWORD;
		const token = localStorage.getItem(AUTHKEY);

		if (isResetPasswordPage) {
			return;
		}

		if (token) {
			if (!menu) {
				fetchMenuDetails();
				return;
			}

			if ((viewDigitalStampStats || viewEsignStats) && userInfo?.isAdmin === null) {
				navigate(routeConstants.STATISTICS, { replace: true });
				dispatch(updateRedirectRoute(routeConstants.STATISTICS));
				return;
			}

			if (Object.keys(menu).length === 0) {
				navigate(routeConstants.PROFILE, { replace: true });
				dispatch(updateRedirectRoute(routeConstants.PROFILE));
				return;
			}

			menuNavigate(menu, userInfo?.userType);
		}
	}, [menu, userInfo, navigate, dispatch]);

	const fetchMenuDetails = async () => {
		try {
			const response = await fetchMenuDetail();
			if (response?.status === 200 && response?.data?.[0]) {
				const menu = response.data[0].permissions;
				const userType = response.data[0].userType;

				dispatch(updateUserInfo({ ...userInfo, menu, userType }));

				if (Object.keys(menu).length === 0) {
					handleApprovalLinkRedirect(menu);
				} else {
					menuNavigate(menu, userType);
				}
			}
		} catch (error) {
			console.error('Failed to fetch menu details', error);
		}
	};

	const handleApprovalLinkRedirect = (menu) => {
		const link = localStorage.getItem('approval_link');
		if (!link) {
			localStorage.removeItem('approval_link');
			navigate(routeConstants.PROFILE, { replace: true });
			dispatch(updateRedirectRoute(routeConstants.PROFILE));
			return;
		}

		const segments = link.trimStart().split('/');
		const redirectPage = segments[2];
		const isValidPage = allowedPages.has(redirectPage);
		const hasPermission = getRedirectPermission(redirectPage, menu);

		if (segments.length > 2 && isValidPage && hasPermission) {
			navigate(`/${redirectPage}`, { replace: true });
		} else {
			navigate(routeConstants.PROFILE, { replace: true });
			dispatch(updateRedirectRoute(routeConstants.PROFILE));
		}
	};

	const menuNavigate = (menu, userType) => {
		const menuItem = Object.keys(menu);
		const sidebarRoutes = sideBarMenu.filter((val) => val.title !== 'Logo');

		const viewStamp = getPermissions(menu, 'Dashboard', 'digital_stamp', 'digital_stamp_statistics');
		const viewEsign = getPermissions(menu, 'Dashboard', 'esign', 'e-sign_statistics');

		handleApprovalLinkRedirect(menu);

		if (['organization', 'customer'].includes(userType) && (viewStamp || viewEsign)) {
			navigate(routeConstants.STATISTICS, { replace: true });
			dispatch(updateRedirectRoute(routeConstants.STATISTICS));
			return;
		}

		const temp = routerData(sidebarRoutes, menu, menuItem);
		if (temp.length > 0) {
			navigate(temp[0].link, { replace: true });
			dispatch(updateRedirectRoute(temp[0].link));
		} else {
			navigate(routeConstants.PROFILE, { replace: true });
			dispatch(updateRedirectRoute(routeConstants.PROFILE));
		}
	};

	return (
		<Suspense fallback={<></>}>
			<Outlet />
		</Suspense>
	);
};

export default UnProtectedLayout;
